import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Container,
  Card,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Grid,
  Button,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { InputAdornment } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../../redux/snackbar/actions";
import { showLoader, hideLoader } from "../../../../redux/loader/actions";
import pricingApi from "../../../../services/product_pricing";
import moment from "moment";

const planColors = [
  "#e0f7fa",
  "#b3e5fc",
  "#c8e6c9",
  "#ffccbc",
  "#f8bbd0",
  "#d1c4e9",
  "#fff9c4",
];

const PlanTable = ({
  planDetails,
  productUUID,
  manageMode,
  priceData,
  setPlanDetails,
  setInitialPricingData,
  initialPricingData,
  setPlanName,
  planName,
  setProductList,
  onGoing,
  resetFields,
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const sortedPlanDetails = [...planDetails].sort(
      (a, b) => a.plan_order - b.plan_order
    );
    const planNames = sortedPlanDetails.map((plan) => plan.product_plan_name);
    setPlanName(planNames);
  }, [planDetails]);

  const plans = [
    "Basic",
    "Standard",
    "Growth",
    "Pro",
    "Enterprise",
    "Pro Plus",
    "Enterprise Plus",
  ];

  const frequencies = ["Monthly", "Quarterly", "Halfyearly", "Yearly"];

  const initialValues = {
    offerApplicable: false,
    offerValidFrom: "",
    offerValidTo: "",
    offerText: "",
    prices: {},
    discounts: {},
    offerPrices: {},
    offerPercentages: {},
  };
  if (plans) {
    plans.forEach((plan) => {
      frequencies.forEach((frequency) => {
        initialValues.prices[`${plan}-${frequency}`] = "";
        initialValues.discounts[`${plan}-${frequency}`] = "";
        initialValues.offerPrices[`${plan}-${frequency}`] = "";
        initialValues.offerPercentages[`${plan}-${frequency}`] = "";
      });
    });
  }

  useEffect(() => {
    if (manageMode !== "create" && priceData) {
      const pricingDetails = priceData.productPlansPricingDetails;
      if (Array.isArray(pricingDetails)) {
        pricingDetails.forEach((detail) => {
          const { plan, frequency, price, discount } = detail;
          initialValues.prices[`${plan}-${frequency}`] = price || "";
          initialValues.discounts[`${plan}-${frequency}`] = discount || "";
          initialValues.offerPrices[`${plan}-${frequency}`] = "";
          initialValues.offerPercentages[`${plan}-${frequency}`] = "";
        });
      }
    }
  }, [priceData]);

  useEffect(() => {
    if (manageMode !== "create" && priceData?.productPlansPricingDetails) {
      const sortedDetails = priceData.productPlansPricingDetails.sort(
        (a, b) => a.productPlan.plan_order - b.productPlan.plan_order
      );

      const updatedPricingData = sortedDetails.reduce(
        (acc, item) => {
          const frequency =
            item.frequency.charAt(0).toUpperCase() + item.frequency.slice(1);
          const planKey = `${item.productPlan.product_plan_name}-${frequency}`;

          acc.prices[planKey] = parseFloat(item.frequency_price) || 0;
          acc.discounts[planKey] = parseFloat(item.frequency_percentage) || 0;

          if (item.is_offer_applicable) {
            acc.offerPrices[planKey] = parseFloat(item.offer_price) || 0;
            acc.offerPercentages[planKey] =
              parseFloat(item.offer_percentage) || 0;
          }

          return acc;
        },
        {
          prices: {},
          discounts: {},
          offerPrices: {},
          offerPercentages: {},
        }
      );

      setInitialPricingData(updatedPricingData);
    }
  }, [priceData, manageMode]);

  if (
    initialPricingData.productPlansPricingDetails &&
    Array.isArray(initialPricingData.productPlansPricingDetails)
  ) {
    initialPricingData.productPlansPricingDetails.forEach((item) => {
      const frequency =
        item.frequency.charAt(0).toUpperCase() + item.frequency.slice(1);
      const planKey = `${item.productPlan.product_plan_name}-${frequency}`;

      initialValues.prices[planKey] = parseFloat(item.frequency_price) || 0;
      initialValues.discounts[planKey] =
        parseFloat(item.frequency_percentage) || 0;

      if (item.is_offer_applicable) {
        initialValues.offerPrices[planKey] = parseFloat(item.offer_price) || 0;
        initialValues.offerPercentages[planKey] =
          parseFloat(item.offer_percentage) || 0;
      }
    });
  }

  let Plan = [];

  if (initialPricingData && initialPricingData.prices) {
    Plan = Object.keys(initialPricingData.prices).reduce((acc, key) => {
      const [planName, frequency] = key.split("-");
      const existingPlan = acc.find((plan) => plan.name === planName);

      if (existingPlan) {
        existingPlan.frequencies.push(frequency);
      } else {
        acc.push({ name: planName, frequencies: [frequency] });
      }

      return acc;
    }, []);
  } else {
    console.error(
      "initialPricingData or initialPricingData.prices is undefined or null"
    );
  }

  const frequencyOrder = ["Monthly", "Quarterly", "Halfyearly", "Yearly"];
  const sortedPlan = Plan.map((plan) => ({
    name: plan.name,
    frequencies: plan.frequencies.sort(
      (a, b) => frequencyOrder.indexOf(a) - frequencyOrder.indexOf(b)
    ),
  }));

  const handleEditSubmit = (values) => {
    dispatch(showLoader());

    const availablePlans = priceData;
    const uuid = availablePlans.prd_plan_package_uuid;
    const data = {
      product_uuid: availablePlans.product.uuid,
      is_offer_applicable: values.offerApplicable || false,
      offer_valid_from: values.offerApplicable
        ? moment
            .utc(values.offerValidFrom)
            .startOf("day")
            .format("YYYY-MM-DD[T]HH:mm:ss[Z]")
        : null,
      offer_valid_to: values.offerApplicable
        ? moment
            .utc(values.offerValidTo)
            .endOf("day")
            .format("YYYY-MM-DD[T]HH:mm:ss[Z]")
        : null,
      offer_text: values.offerApplicable ? values.offerText : "",
      plans: Object.keys(values.discounts).map(
        (frequency_percentage, index) => {
          const frequency = frequency_percentage.split("-")[1].toLowerCase();
          const prdPlanPricingUUID = values.prd_plan_pricing_uuid
            ? values.prd_plan_pricing_uuid[index]
            : null;
          const productPlanUUIDs = Object.keys(values.plan_uuid);
          const groupIndex = Math.floor(index / 4);
          const productPlanUUID =
            productPlanUUIDs[groupIndex % productPlanUUIDs.length];

          return {
            frequency: frequency,
            frequency_percentage: values.discounts[frequency_percentage],
            frequency_price:
              Math.round((values.prices[frequency_percentage] || 0) * 100) /
              100,
            offer_percentage:
              values.offerPercentages[frequency_percentage] || 0,
            offer_price: values.offerPrices[frequency_percentage] || 0,
            is_offer_applicable: values.offerApplicable || false,
            offer_valid_from: values.offerApplicable
              ? values.offerValidFrom
              : null,
            offer_valid_to: values.offerApplicable ? values.offerValidTo : null,
            offer_text: values.offerApplicable ? values.offerText : "",
            product_plan_uuid: productPlanUUID,
            prd_plan_pricing_uuid: prdPlanPricingUUID,
          };
        }
      ),
    };
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.msg || "Info updated successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        // window.location.reload();
        resetFields();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg,
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    pricingApi?.updatePricing(uuid, data).then(onSuccess, onFailure);
  };

  const handleSubmit = (values) => {
    dispatch(showLoader());
    const availablePlans = planDetails;

    const data = {
      product_uuid: productUUID,
      is_offer_applicable: values?.offerApplicable,
      offer_valid_from: values?.offerApplicable
        ? moment
            .utc(values?.offerValidFrom)
            .startOf("day")
            .format("YYYY-MM-DD[T]HH:mm:ss[Z]")
        : null,
      offer_valid_to: values?.offerApplicable
        ? moment
            .utc(values?.offerValidTo)
            .endOf("day")
            .format("YYYY-MM-DD[T]HH:mm:ss[Z]")
        : null,
      offer_text: values?.offerApplicable ? values?.offerText : "",
      plans: [],
    };

    const mapPlansFromValues = (values, frequency, planKey) => {
      const key = `${planKey}-${frequency}`;
      const productPlan = availablePlans.find(
        (plan) => plan.product_plan_name === planKey
      );

      if (!productPlan) {
        console.warn(`No product plan found for key: ${planKey}`);
        return null;
      }

      return {
        product_plan_uuid: productPlan.product_plan_uuid,
        frequency: frequency.toLowerCase().replace(/-/g, ""),
        frequency_percentage: values.discounts[key] || 0,
        frequency_price: Math.round((values.prices[key] || 0) * 100) / 100,
        is_offer_applicable: values.offerApplicable || false,
        offer_valid_from: values.offerApplicable ? values.offerValidFrom : null,
        offer_valid_to: values.offerApplicable ? values.offerValidTo : null,
        offer_text: values.offerApplicable ? values.offerText : "",
        offer_percentage: values.offerApplicable
          ? values.offerPercentages[key] || 0
          : 0,
        offer_price: values.offerApplicable ? values.offerPrices[key] || "" : 0,
      };
    };
    const planNames = [
      ...new Set(Object.keys(values.prices).map((key) => key.split("-")[0])),
    ];

    const frequencies = ["Monthly", "Quarterly", "Halfyearly", "Yearly"];
    planNames.forEach((planKey) => {
      frequencies.forEach((frequency) => {
        const mappedPlan = mapPlansFromValues(values, frequency, planKey);
        if (mappedPlan) {
          data.plans.push(mappedPlan);
        }
      });
    });

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.msg || "Info updated successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
      }
      // window.location.reload();
      resetFields();
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg,
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    pricingApi?.createPricing(data).then(onSuccess, onFailure);
  };

  const validationSchema = Yup.object({
    offerApplicable: Yup.boolean().nullable(),

    offerValidFrom: Yup.date()
      .nullable()
      .test(
        "is-valid-from-required",
        "Offer valid from is required when offer is applicable",
        function (value) {
          const { offerApplicable } = this.parent;
          if (offerApplicable) {
            return value && value !== "" && !isNaN(new Date(value).getTime());
          }
          return true;
        }
      ),

    offerValidTo: Yup.date()
      .nullable()
      .test(
        "is-valid-to-required",
        "Offer valid to is required when offer is applicable",
        function (value) {
          const { offerApplicable } = this.parent;
          if (offerApplicable) {
            return value && value !== "" && !isNaN(new Date(value).getTime());
          }
          return true;
        }
      )
      .min(Yup.ref("offerValidFrom"), "End date must be after start date"),

    offerText: Yup.string()
      .test("is-text-required", "Offer text is required", function (value) {
        const { offerApplicable } = this.parent;
        return !offerApplicable || (value && value.trim() !== "");
      })
      .nullable(),

    discounts: Yup.object()
      .shape(
        planName.reduce((acc, plan) => {
          frequencies.forEach((frequency) => {
            acc[`${plan}-${frequency}`] = Yup.number()
              .min(0, "Discount cannot be negative")
              .max(100, "Discount cannot exceed 100%");
          });
          return acc;
        }, {})
      )
      .nullable(),

    prices: Yup.object()
      .shape(
        planName.reduce((acc, plan) => {
          acc[`${plan}-Monthly`] = Yup.number()
            .required("Monthly price is required")
            .positive("Monthly price must be positive");

          frequencies.forEach((frequency) => {
            acc[`${plan}-${frequency}`] = Yup.number()
              .required(`${frequency} price is required`)
              .positive(`${frequency} price must be positive`)
              .min(0, "Price cannot be negative");
          });
          return acc;
        }, {})
      )
      .test(
        "are-prices-required",
        "Prices are required when offer is not applicable.",
        function (value) {
          const { offerApplicable } = this.parent;
          return (
            offerApplicable ||
            (value &&
              Object.values(value).some((v) => v !== undefined && v !== null))
          );
        }
      )
      .required("Prices object is required"),

    offerPrices: Yup.object()
      .shape(
        planName.reduce((acc, plan) => {
          frequencies.forEach((frequency) => {
            acc[`${plan}-${frequency}`] = Yup.number()
              .positive("Offer price must be positive")
              .test(
                "is-less-or-equal-to-base-price",
                "Offer price cannot exceed regular price.",
                function (value) {
                  const { prices, offerApplicable } = this.parent;
                  if (!offerApplicable) return true;

                  if (prices && prices[`${plan}-${frequency}`] !== undefined) {
                    const basePrice = prices[`${plan}-${frequency}`];
                    return value <= basePrice;
                  }
                  return true;
                }
              );
          });
          return acc;
        }, {})
      )
      .test(
        "are-offer-prices-required",
        "Offer prices are required when offer is applicable.",
        function (value) {
          const { offerApplicable } = this.parent;
          return (
            !offerApplicable ||
            (value &&
              Object.values(value).some((v) => v !== undefined && v !== null))
          );
        }
      )
      .nullable(),

    offerPercentages: Yup.object()
      .shape(
        planName.reduce((acc, plan) => {
          frequencies.forEach((frequency) => {
            acc[`${plan}-${frequency}`] = Yup.number()
              .min(0, "Offer percentage cannot be negative")
              .max(100, "Offer percentage cannot exceed 100%");
          });
          return acc;
        }, {})
      )
      .test(
        "are-offer-percentages-required",
        "Offer percentages are required when offer is applicable.",
        function (value) {
          const { offerApplicable } = this.parent;
          return (
            !offerApplicable ||
            (value &&
              Object.values(value).some((v) => v !== undefined && v !== null))
          );
        }
      )
      .nullable(),
  });
  const validateForm = async (formData) => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      if (manageMode === "create") {
        handleSubmit(formData);
      } else {
        handleEditSubmit(formData);
      }
    } catch (err) {
      const errorMessages = err.inner.map((error) => error.message);
      dispatch(
        showSnackbar({
          message: "All fields are required",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "warning",
        })
      );
    }
  };

  return (
    <Container maxWidth="xl">
      <Card elevation={3}>
        {manageMode === "edit" || manageMode === "view" ? (
          <CardHeader
            title={priceData && priceData.product.name}
            sx={{ paddingBottom: "0" }}
          />
        ) : null}
        <Formik
          initialValues={{
            offerApplicable:
              manageMode !== "create" && priceData?.is_offer_applicable,
            offerValidFrom:
              manageMode !== "create" && priceData?.is_offer_applicable
                ? isNaN(moment.utc(priceData.offer_valid_from).startOf("day"))
                  ? null
                  : moment
                      .utc(priceData.offer_valid_from)
                      .startOf("day")
                      .format("YYYY-MM-DD")
                : null,
            offerValidTo:
              manageMode !== "create" && priceData?.is_offer_applicable
                ? isNaN(moment.utc(priceData.offer_valid_to).endOf("day"))
                  ? null
                  : moment
                      .utc(priceData.offer_valid_to)
                      .endOf("day")
                      .format("YYYY-MM-DD")
                : null,
            offerText:
              manageMode !== "create" && priceData?.is_offer_applicable
                ? priceData?.offer_text
                : "",
            prices:
              manageMode !== "create" ? initialPricingData.prices || {} : {},
            discounts:
              manageMode !== "create" ? initialPricingData.discounts || {} : {},
            offerPrices:
              manageMode !== "create"
                ? initialPricingData.offerPrices || {}
                : {},
            offerPercentages:
              manageMode !== "create"
                ? initialPricingData.offerPercentages || {}
                : {},
            plan_uuid:
              manageMode !== "create" &&
              Array.isArray(priceData?.productPlansPricingDetails)
                ? priceData.productPlansPricingDetails.reduce((acc, data) => {
                    const uuid = data.productPlan.product_plan_uuid;
                    acc[uuid] = (acc[uuid] || 0) + 1;
                    return acc;
                  }, {})
                : {},
            prd_plan_pricing_uuid:
              manageMode !== "create" && priceData?.productPlansPricingDetails
                ? priceData?.productPlansPricingDetails.map(
                    (data) => data.prd_plan_pricing_uuid
                  )
                : {},
          }}
          validateOnChange={true}
          enableReinitialize={true}
          validateOnBlur={true}
          // validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, setErrors, errors }) => {
            const formErrors = { ...errors };
            const customErrors = {};
            if (values.offerApplicable && !values.offerValidFrom) {
              customErrors.offerValidFrom = "Offer valid from is required.";
            }
            if (values.offerApplicable && !values.offerValidTo) {
              customErrors.offerValidTo = "Offer valid to is required.";
            }
            if (values.offerApplicable && !values.offerText) {
              customErrors.offerText = "Offer text is required.";
            }
            if (
              values.offerApplicable &&
              Object.keys(values.prices).length === 0
            ) {
              customErrors.prices = "Prices are required.";
            }
            if (
              values.offerApplicable &&
              Object.keys(values.discounts).length === 0
            ) {
              customErrors.discounts = "Discounts are required.";
            }
            if (
              values.offerApplicable &&
              Object.keys(values.offerPrices).length === 0
            ) {
              customErrors.offerPrices = "Offer prices are required.";
            }
            if (
              values.offerApplicable &&
              Object.keys(values.offerPercentages).length === 0
            ) {
              customErrors.offerPercentages = "Offer percentages are required.";
            }
            const allErrors = { ...formErrors, ...customErrors };
            if (Object.keys(allErrors).length > 0) {
              setErrors(allErrors);
              dispatch(
                showSnackbar({
                  message: "Fill all the fields",
                  autoHideDuration: 3000,
                  anchorOrigin: {
                    vertical: "top",
                    horizontal: "right",
                  },
                  variant: "warning",
                })
              );
            } else {
              if (manageMode === "create") {
                // handleSubmit(values);
                validateForm(values);
              } else {
                // handleEditSubmit(values);
                validateForm(values);
              }
              // validateForm(values);
            }

            setSubmitting(false);
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2} sx={{ padding: 2 }}>
                <Grid item xs={12} sm={12} md={3}>
                  {planDetails?.length > 0 &&
                    manageMode !== "view" &&
                    manageMode !== "ongoing" && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.offerApplicable}
                            onChange={(event) => {
                              const isChecked = event.target.checked;
                              setFieldValue("offerApplicable", isChecked);

                              if (!isChecked && manageMode === "create") {
                                setFieldValue("offerValidFrom", "");
                                setFieldValue("offerValidTo", "");
                                setFieldValue("offerText", "");
                                planName.forEach((plan) => {
                                  frequencies.forEach((frequency) => {
                                    setFieldValue(
                                      `prices.${plan}-${frequency}`,
                                      ""
                                    );
                                    setFieldValue(
                                      `discounts.${plan}-${frequency}`,
                                      ""
                                    );
                                    setFieldValue(
                                      `offerPrices.${plan}-${frequency}`,
                                      ""
                                    );
                                    setFieldValue(
                                      `offerPercentages.${plan}-${frequency}`,
                                      ""
                                    );
                                  });
                                });
                              } else {
                                if (manageMode === "create") {
                                  planName.forEach((plan) => {
                                    frequencies.forEach((frequency) => {
                                      const basePrice =
                                        values.prices[`${plan}-${frequency}`] ||
                                        0;
                                      // setFieldValue(
                                      //   `offerPrices.${plan}-${frequency}`,
                                      //   basePrice
                                      // );
                                    });
                                  });
                                }
                              }
                            }}
                          />
                        }
                        label="Offer Applicable"
                        sx={{ marginBottom: 2 }}
                      />
                    )}
                </Grid>
                {values.offerApplicable && (
                  <>
                    <Grid item xs={12} sm={12} md={3}>
                      <Field name="offerValidFrom">
                        {({ field, meta }) => (
                          <TextField
                            type="date"
                            {...field}
                            label="Offer Valid From"
                            InputLabelProps={{ shrink: true }}
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched && meta.error}
                            InputProps={{
                              readOnly:
                                manageMode === "view" ||
                                manageMode === "ongoing",
                            }}
                            inputProps={{
                              min:
                                manageMode === "create" ||
                                manageMode === "edit" ||
                                (manageMode !== "create" &&
                                  (!priceData?.offer_valid_from ||
                                    isNaN(
                                      new Date(priceData.offer_valid_from)
                                    )))
                                  ? new Date().toISOString().split("T")[0]
                                  : undefined,
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3}>
                      <Field name="offerValidTo">
                        {({ field, meta }) => (
                          <TextField
                            type="date"
                            {...field}
                            label="Offer Valid To"
                            InputLabelProps={{ shrink: true }}
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched && meta.error}
                            InputProps={{
                              readOnly:
                                manageMode === "view" ||
                                manageMode === "ongoing",
                            }}
                            inputProps={{
                              min: values.offerValidFrom
                                ? moment(values.offerValidFrom).format(
                                    "YYYY-MM-DD"
                                  )
                                : new Date().toISOString().split("T")[0],
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3}>
                      <Field name="offerText">
                        {({ field, meta }) => (
                          <TextField
                            {...field}
                            label="Offer Text"
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched && meta.error}
                            InputProps={{
                              readOnly:
                                manageMode === "view" ||
                                manageMode === "ongoing",
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                  </>
                )}
              </Grid>
              <TableContainer>
                {planDetails?.length > 0 || Plan.length > 0 ? (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" rowSpan={2}>
                          Plan
                        </TableCell>
                        <TableCell align="center" rowSpan={2}>
                          Frequency
                        </TableCell>
                        <TableCell align="center" rowSpan={2}>
                          Frequency Discount (%)
                        </TableCell>
                        <TableCell align="center" rowSpan={2}>
                          Price (INR)
                        </TableCell>
                        {values.offerApplicable && (
                          <TableCell align="center" rowSpan={2}>
                            Offer Price (INR)
                          </TableCell>
                        )}
                        {values.offerApplicable && (
                          <TableCell align="center" rowSpan={2}>
                            Offer Percentage (%)
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    {manageMode === "create" && (
                      <TableBody>
                        {planName.map((plan, index) =>
                          frequencies.map((frequency, freqIndex) => (
                            <TableRow key={`${index}-${freqIndex}`}>
                              {freqIndex === 0 && (
                                <TableCell
                                  align="center"
                                  rowSpan={frequencies.length}
                                  sx={{
                                    backgroundColor:
                                      planColors[index % planColors.length],
                                    textAlign: "center",
                                  }}
                                >
                                  {plan.charAt(0).toUpperCase() + plan.slice(1)}
                                </TableCell>
                              )}
                              <TableCell align="center">{frequency}</TableCell>
                              {frequency !== "Monthly" ? (
                                <TableCell align="center">
                                  <Field
                                    name={`discounts.${plan}-${frequency}`}
                                  >
                                    {({ field, meta }) => {
                                      const currentDiscountValue =
                                        values.discounts[
                                          `${plan}-${frequency}`
                                        ] || "";

                                      return (
                                        <TextField
                                          {...field}
                                          variant="outlined"
                                          error={
                                            meta.touched && Boolean(meta.error)
                                          }
                                          helperText={
                                            meta.touched && meta.error
                                              ? meta.error
                                              : ""
                                          }
                                          onChange={(e) => {
                                            const discountValue =
                                              parseFloat(e.target.value) || 0;

                                            if (discountValue === 100) {
                                              dispatch(
                                                showSnackbar({
                                                  message:
                                                    "Discount value cannot be 100%.",
                                                  autoHideDuration: 3000,
                                                  anchorOrigin: {
                                                    vertical: "top",
                                                    horizontal: "right",
                                                  },
                                                  variant: "warning",
                                                })
                                              );
                                              setFieldValue(
                                                `discounts.${plan}-${frequency}`,
                                                initialDiscountValue
                                              );
                                              return;
                                            }
                                            const initialDiscountValue =
                                              values.discounts[
                                                `${plan}-${frequency}`
                                              ] || 0;
                                            const basePrice =
                                              values.prices[
                                                `${plan}-${frequency}`
                                              ] || 0;
                                            const initialDiscountedPrice =
                                              values.prices[
                                                `${plan}-${frequency}`
                                              ] || basePrice;
                                            const discountedPrice =
                                              basePrice *
                                              (1 - discountValue / 100);
                                            setFieldValue(
                                              `discounts.${plan}-${frequency}`,
                                              discountValue
                                            );
                                            setFieldValue(
                                              `prices.${plan}-${frequency}`,
                                              discountedPrice
                                            );
                                            const monthlyPrice =
                                              values.prices[
                                                `${plan}-Monthly`
                                              ] || 0;
                                            if (frequency === "Quarterly") {
                                              const quarterlyDiscountedPrice =
                                                monthlyPrice *
                                                3 *
                                                (1 - discountValue / 100);
                                              setFieldValue(
                                                `prices.${plan}-Quarterly`,
                                                quarterlyDiscountedPrice
                                              );
                                            } else if (
                                              frequency === "Halfyearly"
                                            ) {
                                              const halfYearlyDiscountedPrice =
                                                monthlyPrice *
                                                6 *
                                                (1 - discountValue / 100);
                                              setFieldValue(
                                                `prices.${plan}-Halfyearly`,
                                                halfYearlyDiscountedPrice
                                              );
                                            } else if (frequency === "Yearly") {
                                              const yearlyDiscountedPrice =
                                                monthlyPrice *
                                                12 *
                                                (1 - discountValue / 100);
                                              setFieldValue(
                                                `prices.${plan}-Yearly`,
                                                yearlyDiscountedPrice
                                              );
                                            }
                                          }}
                                          value={currentDiscountValue}
                                          InputProps={{
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                %
                                              </InputAdornment>
                                            ),
                                          }}
                                          sx={{
                                            width: "100%",
                                            textAlign: "center",
                                          }}
                                        />
                                      );
                                    }}
                                  </Field>
                                </TableCell>
                              ) : (
                                <TableCell align="center">-</TableCell>
                              )}
                              <TableCell align="center">
                                <Field name={`prices.${plan}-${frequency}`}>
                                  {({ field, meta }) => {
                                    const currentPriceValue = values.prices
                                      ? values.prices[
                                          `${plan}-${frequency}`
                                        ] !== undefined
                                        ? Math.floor(
                                            values.prices[
                                              `${plan}-${frequency}`
                                            ] * 100
                                          ) / (100).toFixed(2)
                                        : "0"
                                      : "0";
                                    return (
                                      <TextField
                                        {...field}
                                        variant="outlined"
                                        error={
                                          meta.touched && Boolean(meta.error)
                                        }
                                        helperText={
                                          meta.touched && meta.error
                                            ? meta.error
                                            : ""
                                        }
                                        value={currentPriceValue}
                                        onChange={(e) => {
                                          const price =
                                            parseInt(e.target.value, 10) || 0;
                                          setFieldValue(
                                            `prices.${plan}-${frequency}`,
                                            price
                                          );
                                          setFieldValue(
                                            `discounts.${plan}-Monthly`,
                                            0
                                          );
                                          setFieldValue(
                                            `discounts.${plan}-Quarterly`,
                                            0
                                          );
                                          setFieldValue(
                                            `discounts.${plan}-Halfyearly`,
                                            0
                                          );
                                          setFieldValue(
                                            `discounts.${plan}-Yearly`,
                                            0
                                          );
                                          if (frequency === "Monthly") {
                                            const quarterlyPrice = Math.floor(
                                              price * 3
                                            );
                                            const halfYearlyPrice = Math.floor(
                                              price * 6
                                            );
                                            const yearlyPrice = Math.floor(
                                              price * 12
                                            );

                                            setFieldValue(
                                              `prices.${plan}-Quarterly`,
                                              quarterlyPrice
                                            );
                                            setFieldValue(
                                              `prices.${plan}-Halfyearly`,
                                              halfYearlyPrice
                                            );
                                            setFieldValue(
                                              `prices.${plan}-Yearly`,
                                              yearlyPrice
                                            );
                                          }
                                        }}
                                        InputProps={{
                                          readOnly: frequency !== "Monthly",
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              ₹
                                            </InputAdornment>
                                          ),
                                        }}
                                        sx={{
                                          width: "100%",
                                          textAlign: "center",
                                        }}
                                      />
                                    );
                                  }}
                                </Field>
                              </TableCell>

                              {values.offerApplicable && (
                                <>
                                  <TableCell align="center">
                                    <Field
                                      name={`offerPrices.${plan}-${frequency}`}
                                    >
                                      {({ field, meta }) => (
                                        <TextField
                                          {...field}
                                          variant="outlined"
                                          error={
                                            meta.touched && Boolean(meta.error)
                                          }
                                          helperText={
                                            meta.touched && meta.error
                                              ? meta.error
                                              : ""
                                          }
                                          value={
                                            values.offerPrices[
                                              `${plan}-${frequency}`
                                            ] || ""
                                          }
                                          onChange={(e) => {
                                            const offerPrice =
                                              parseInt(e.target.value, 10) || 0;
                                            const basePrice =
                                              values.prices[
                                                `${plan}-${frequency}`
                                              ] || 0;
                                            if (offerPrice > basePrice) {
                                              dispatch(
                                                showSnackbar({
                                                  message:
                                                    "Offer price cannot exceed the regular price.",
                                                  autoHideDuration: 3000,
                                                  anchorOrigin: {
                                                    vertical: "top",
                                                    horizontal: "right",
                                                  },
                                                  variant: "warning",
                                                })
                                              );
                                            }

                                            setFieldValue(
                                              `offerPrices.${plan}-${frequency}`,
                                              offerPrice
                                            );

                                            if (basePrice > 0) {
                                              const offerPercentage =
                                                ((basePrice - offerPrice) /
                                                  basePrice) *
                                                100;
                                              setFieldValue(
                                                `offerPercentages.${plan}-${frequency}`,
                                                offerPercentage
                                              );
                                            }
                                          }}
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                ₹
                                              </InputAdornment>
                                            ),
                                          }}
                                          sx={{
                                            width: "120px",
                                            textAlign: "center",
                                            marginRight: 1,
                                          }}
                                        />
                                      )}
                                    </Field>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Field
                                      name={`offerPercentages.${plan}-${frequency}`}
                                    >
                                      {({ field, meta }) => (
                                        <TextField
                                          {...field}
                                          variant="outlined"
                                          value={
                                            values.offerPercentages[
                                              `${plan}-${frequency}`
                                            ] || ""
                                          }
                                          error={
                                            meta.touched && Boolean(meta.error)
                                          }
                                          helperText={
                                            meta.touched && meta.error
                                              ? meta.error
                                              : ""
                                          }
                                          onChange={(e) => {
                                            const inputValue = e.target.value;
                                            if (
                                              /^\d{0,3}(\.\d{0,2})?$/.test(
                                                inputValue
                                              ) ||
                                              inputValue === ""
                                            ) {
                                              if (inputValue === "") {
                                                setFieldValue(
                                                  `offerPercentages.${plan}-${frequency}`,
                                                  ""
                                                );
                                                setFieldValue(
                                                  `offerPrices.${plan}-${frequency}`,
                                                  0
                                                );
                                              } else {
                                                const offerPercentage =
                                                  parseFloat(inputValue);

                                                if (
                                                  offerPercentage >= 0 &&
                                                  offerPercentage <= 100
                                                ) {
                                                  setFieldValue(
                                                    `offerPercentages.${plan}-${frequency}`,
                                                    inputValue
                                                  );
                                                  const basePrice =
                                                    values.prices[
                                                      `${plan}-${frequency}`
                                                    ] || 0;
                                                  const calculatedOfferPrice =
                                                    basePrice -
                                                    (basePrice *
                                                      offerPercentage) /
                                                      100;

                                                  setFieldValue(
                                                    `offerPrices.${plan}-${frequency}`,
                                                    isNaN(calculatedOfferPrice)
                                                      ? 0
                                                      : calculatedOfferPrice
                                                  );
                                                } else if (
                                                  offerPercentage > 100
                                                ) {
                                                  dispatch(
                                                    showSnackbar({
                                                      message:
                                                        "Offer percentage cannot exceed 100%",
                                                      autoHideDuration: 3000,
                                                      anchorOrigin: {
                                                        vertical: "top",
                                                        horizontal: "right",
                                                      },
                                                      variant: "warning",
                                                    })
                                                  );
                                                }
                                              }
                                            }
                                          }}
                                          onBlur={() => {
                                            const value =
                                              values.offerPercentages[
                                                `${plan}-${frequency}`
                                              ];
                                            if (
                                              value !== undefined &&
                                              value !== ""
                                            ) {
                                              setFieldValue(
                                                `offerPercentages.${plan}-${frequency}`,
                                                parseFloat(value).toFixed(2)
                                              );
                                            }
                                          }}
                                          InputProps={{
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                %
                                              </InputAdornment>
                                            ),
                                          }}
                                          sx={{
                                            width: "120px",
                                            textAlign: "center",
                                            marginRight: 1,
                                          }}
                                        />
                                      )}
                                    </Field>
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    )}

                    {manageMode === "edit" && (
                      <TableBody>
                        {Plan && Plan.length > 0 ? (
                          Plan.map((plan, index) =>
                            plan.frequencies.map((frequency, freqIndex) => (
                              <TableRow key={`${index}-${freqIndex}`}>
                                {freqIndex === 0 && (
                                  <TableCell
                                    align="center"
                                    rowSpan={plan.frequencies.length}
                                    sx={{
                                      backgroundColor:
                                        planColors[index % planColors.length],
                                      textAlign: "center",
                                    }}
                                  >
                                    {plan.name.charAt(0).toUpperCase() +
                                      plan.name.slice(1)}
                                  </TableCell>
                                )}
                                <TableCell align="center">
                                  {frequency}
                                </TableCell>
                                {frequency !== "Monthly" ? (
                                  <TableCell align="center">
                                    <Field
                                      name={`discounts.${plan.name}-${frequency}`}
                                    >
                                      {({ field, meta }) => {
                                        const currentDiscountValue =
                                          values.discounts[
                                            `${plan.name}-${frequency}`
                                          ] || "";

                                        return (
                                          <TextField
                                            {...field}
                                            variant="outlined"
                                            error={
                                              meta.touched &&
                                              Boolean(meta.error)
                                            }
                                            helperText={
                                              meta.touched && meta.error
                                                ? meta.error
                                                : ""
                                            }
                                            onChange={(e) => {
                                              const discountValue =
                                                e.target.value === ""
                                                  ? ""
                                                  : parseFloat(
                                                      e.target.value
                                                    ) || 0;
                                              if (discountValue > 100) {
                                                dispatch(
                                                  showSnackbar({
                                                    message:
                                                      "Discount cannot exceed 100%",
                                                    autoHideDuration: 3000,
                                                    anchorOrigin: {
                                                      vertical: "top",
                                                      horizontal: "right",
                                                    },
                                                    variant: "warning",
                                                  })
                                                );
                                                return;
                                              }
                                              if (e.target.value === "") {
                                                const initialMonthlyPrice =
                                                  values.prices[
                                                    `${plan.name}-Monthly`
                                                  ] || 0;
                                                setFieldValue(
                                                  `discounts.${plan.name}-${frequency}`,
                                                  0
                                                );
                                                setFieldValue(
                                                  `prices.${plan.name}-${frequency}`,
                                                  initialMonthlyPrice *
                                                    (frequency === "Monthly"
                                                      ? 1
                                                      : frequency ===
                                                        "Quarterly"
                                                      ? 3
                                                      : frequency ===
                                                        "Halfyearly"
                                                      ? 6
                                                      : 12)
                                                );
                                                return;
                                              }
                                              const monthlyPrice =
                                                values.prices[
                                                  `${plan.name}-Monthly`
                                                ] || 0;
                                              const calculatedPrice =
                                                monthlyPrice *
                                                (frequency === "Monthly"
                                                  ? 1
                                                  : frequency === "Quarterly"
                                                  ? 3
                                                  : frequency === "Halfyearly"
                                                  ? 6
                                                  : 12);
                                              const discountedPrice =
                                                calculatedPrice *
                                                (1 - discountValue / 100);
                                              setFieldValue(
                                                `discounts.${plan.name}-${frequency}`,
                                                discountValue
                                              );
                                              setFieldValue(
                                                `prices.${plan.name}-${frequency}`,
                                                discountedPrice
                                              );
                                            }}
                                            value={currentDiscountValue}
                                            InputProps={{
                                              endAdornment: (
                                                <InputAdornment position="end">
                                                  %
                                                </InputAdornment>
                                              ),
                                            }}
                                            sx={{
                                              width: "100%",
                                              textAlign: "center",
                                            }}
                                          />
                                        );
                                      }}
                                    </Field>
                                  </TableCell>
                                ) : (
                                  <TableCell align="center">-</TableCell>
                                )}
                                <TableCell align="center">
                                  <Field
                                    name={`prices.${plan.name}-${frequency}`}
                                  >
                                    {({ field, meta }) => {
                                      // const currentPriceValue =
                                      //   values.prices[
                                      //   `${plan.name}-${frequency}`
                                      //   ] || ""; // Pull current value from Formik

                                      const currentPriceValue =
                                        values.prices &&
                                        values.prices[
                                          `${plan.name}-${frequency}`
                                        ] !== undefined
                                          ? (
                                              Math.floor(
                                                values.prices[
                                                  `${plan.name}-${frequency}`
                                                ] * 100
                                              ) / 100
                                            )
                                              .toString()
                                              .replace(/\.00$/, "")
                                          : "";

                                      return (
                                        <TextField
                                          {...field}
                                          variant="outlined"
                                          error={
                                            meta.touched && Boolean(meta.error)
                                          }
                                          helperText={
                                            meta.touched && meta.error
                                              ? meta.error
                                              : ""
                                          }
                                          value={currentPriceValue}
                                          onChange={(e) => {
                                            const price =
                                              parseInt(e.target.value, 10) || 0;

                                            setFieldValue(
                                              `prices.${plan.name}-${frequency}`,
                                              price
                                            );
                                            [
                                              "Monthly",
                                              "Quarterly",
                                              "Halfyearly",
                                              "Yearly",
                                            ].forEach((freq) => {
                                              setFieldValue(
                                                `offerPercentages.${plan.name}-${freq}`,
                                                ""
                                              );
                                              setFieldValue(
                                                `offerPrices.${plan.name}-${freq}`,
                                                0
                                              );
                                            });
                                            const frequencyMultiplier = {
                                              Monthly: {
                                                Quarterly: 3,
                                                Halfyearly: 6,
                                                Yearly: 12,
                                              },
                                              Quarterly: {
                                                Monthly: price / 3,
                                                Halfyearly: price * 2,
                                                Yearly: price * 4,
                                              },
                                              Halfyearly: {
                                                Monthly: price / 6,
                                                Quarterly: price / 2,
                                                Yearly: price * 2,
                                              },
                                              Yearly: {
                                                Monthly: price / 12,
                                                Quarterly: price / 4,
                                                Halfyearly: price / 2,
                                              },
                                            }[frequency];

                                            Object.keys(
                                              frequencyMultiplier
                                            ).forEach((key) => {
                                              const calculatedPrice =
                                                Math.floor(
                                                  price *
                                                    frequencyMultiplier[key]
                                                );
                                              setFieldValue(
                                                `prices.${plan.name}-${key}`,
                                                calculatedPrice
                                              );
                                            });
                                            setFieldValue(
                                              `discounts.${plan.name}-Monthly`,
                                              0
                                            );
                                            setFieldValue(
                                              `discounts.${plan.name}-Quarterly`,
                                              0
                                            );
                                            setFieldValue(
                                              `discounts.${plan.name}-Halfyearly`,
                                              0
                                            );
                                            setFieldValue(
                                              `discounts.${plan.name}-Yearly`,
                                              0
                                            );
                                          }}
                                          InputProps={{
                                            readOnly: frequency !== "Monthly",
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                ₹
                                              </InputAdornment>
                                            ),
                                          }}
                                          sx={{
                                            width: "100%",
                                            textAlign: "center",
                                          }}
                                          type="number"
                                          inputProps={{
                                            step: 1,
                                            min: 0,
                                          }}
                                        />
                                      );
                                    }}
                                  </Field>
                                </TableCell>
                                {values.offerApplicable && (
                                  <>
                                    <TableCell align="center">
                                      <Field
                                        name={`offerPrices.${plan.name}-${frequency}`}
                                      >
                                        {({ field, meta }) => {
                                          const currentOfferPriceValue =
                                            values.offerPrices[
                                              `${plan.name}-${frequency}`
                                            ] || "";

                                          return (
                                            <TextField
                                              {...field}
                                              variant="outlined"
                                              error={
                                                meta.touched &&
                                                Boolean(meta.error)
                                              }
                                              helperText={
                                                meta.touched && meta.error
                                                  ? meta.error
                                                  : ""
                                              }
                                              value={currentOfferPriceValue}
                                              onChange={(e) => {
                                                const offerPrice =
                                                  parseInt(
                                                    e.target.value,
                                                    10
                                                  ) || 0;
                                                const basePrice =
                                                  values.prices[
                                                    `${plan.name}-${frequency}`
                                                  ] || 0;
                                                if (offerPrice > basePrice) {
                                                  dispatch(
                                                    showSnackbar({
                                                      message:
                                                        "Offer price cannot exceed the regular price.",
                                                      autoHideDuration: 3000,
                                                      anchorOrigin: {
                                                        vertical: "top",
                                                        horizontal: "right",
                                                      },
                                                      variant: "warning",
                                                    })
                                                  );
                                                }

                                                setFieldValue(
                                                  `offerPrices.${plan.name}-${frequency}`,
                                                  offerPrice
                                                );

                                                if (basePrice > 0) {
                                                  const offerPercentage =
                                                    ((basePrice - offerPrice) /
                                                      basePrice) *
                                                    100;
                                                  setFieldValue(
                                                    `offerPercentages.${plan.name}-${frequency}`,
                                                    offerPercentage
                                                  );
                                                }
                                              }}
                                              InputProps={{
                                                startAdornment: (
                                                  <InputAdornment position="start">
                                                    ₹
                                                  </InputAdornment>
                                                ),
                                              }}
                                              sx={{
                                                width: "120px",
                                                textAlign: "center",
                                                marginRight: 1,
                                              }}
                                              type="number"
                                              inputProps={{
                                                step: 1,
                                                min: 0,
                                              }}
                                            />
                                          );
                                        }}
                                      </Field>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Field
                                        name={`offerPercentages.${plan.name}-${frequency}`}
                                      >
                                        {({ field, meta }) => {
                                          const basePrice =
                                            values.prices[
                                              `${plan.name}-${frequency}`
                                            ] || 0;

                                          return (
                                            <TextField
                                              {...field}
                                              variant="outlined"
                                              error={
                                                meta.touched &&
                                                Boolean(meta.error)
                                              }
                                              helperText={
                                                meta.touched && meta.error
                                                  ? meta.error
                                                  : ""
                                              }
                                              value={field.value || ""}
                                              onChange={(e) => {
                                                const inputValue =
                                                  e.target.value;
                                                if (inputValue === "") {
                                                  setFieldValue(
                                                    `offerPercentages.${plan.name}-${frequency}`,
                                                    ""
                                                  );
                                                  setFieldValue(
                                                    `offerPrices.${plan.name}-${frequency}`,
                                                    0
                                                  );
                                                  return;
                                                }

                                                const offerPercentage =
                                                  parseFloat(inputValue);

                                                if (!isNaN(offerPercentage)) {
                                                  if (
                                                    offerPercentage >= 0 &&
                                                    offerPercentage <= 100
                                                  ) {
                                                    setFieldValue(
                                                      `offerPercentages.${plan.name}-${frequency}`,
                                                      offerPercentage
                                                    );

                                                    const calculatedOfferPrice =
                                                      basePrice -
                                                      (basePrice *
                                                        offerPercentage) /
                                                        100;
                                                    setFieldValue(
                                                      `offerPrices.${plan.name}-${frequency}`,
                                                      calculatedOfferPrice
                                                    );
                                                  } else if (
                                                    offerPercentage > 100
                                                  ) {
                                                    dispatch(
                                                      showSnackbar({
                                                        message:
                                                          "Offer percentage cannot exceed 100%",
                                                        autoHideDuration: 3000,
                                                        anchorOrigin: {
                                                          vertical: "top",
                                                          horizontal: "right",
                                                        },
                                                        variant: "warning",
                                                      })
                                                    );
                                                  }
                                                }
                                              }}
                                              InputProps={{
                                                endAdornment: (
                                                  <InputAdornment position="end">
                                                    %
                                                  </InputAdornment>
                                                ),
                                              }}
                                              sx={{
                                                width: "120px",
                                                textAlign: "center",
                                                marginRight: 1,
                                              }}
                                            />
                                          );
                                        }}
                                      </Field>
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            ))
                          )
                        ) : (
                          <TableRow>
                            <TableCell align="center">
                              No plans available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    )}

                    {manageMode === "view" && (
                      <TableBody>
                        {Plan && Plan.length > 0 ? (
                          Plan.map((plan, index) =>
                            plan.frequencies.map((frequency, freqIndex) => (
                              <TableRow key={`${index}-${freqIndex}`}>
                                {freqIndex === 0 && (
                                  <TableCell
                                    align="center"
                                    rowSpan={plan.frequencies.length}
                                    sx={{
                                      backgroundColor:
                                        planColors[index % planColors.length],
                                      textAlign: "center",
                                    }}
                                  >
                                    {plan.name.charAt(0).toUpperCase() +
                                      plan.name.slice(1)}
                                  </TableCell>
                                )}
                                <TableCell align="center">
                                  {frequency}
                                </TableCell>
                                {frequency !== "Monthly" ? (
                                  <TableCell align="center">
                                    {initialPricingData.discounts[
                                      `${plan.name}-${frequency}`
                                    ] || "-"}
                                  </TableCell>
                                ) : (
                                  <TableCell align="center">-</TableCell>
                                )}
                                <TableCell align="center">
                                  {initialPricingData.prices[
                                    `${plan.name}-${frequency}`
                                  ] || "-"}
                                </TableCell>
                                {values.offerApplicable && (
                                  <>
                                    <TableCell align="center">
                                      {initialPricingData.offerPrices[
                                        `${plan.name}-${frequency}`
                                      ] || "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                      {initialPricingData.offerPercentages[
                                        `${plan.name}-${frequency}`
                                      ]
                                        ? parseFloat(
                                            initialPricingData.offerPercentages[
                                              `${plan.name}-${frequency}`
                                            ]
                                          ).toFixed(2)
                                        : "-"}
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            ))
                          )
                        ) : (
                          <TableRow>
                            <TableCell align="center">
                              No plans available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    )}
                    {manageMode === "ongoing" && (
                      <TableBody>
                        {Plan && Plan.length > 0 ? (
                          Plan.map((plan, index) =>
                            plan.frequencies.map((frequency, freqIndex) => (
                              <TableRow key={`${index}-${freqIndex}`}>
                                {freqIndex === 0 && (
                                  <TableCell
                                    align="center"
                                    rowSpan={plan.frequencies.length}
                                    sx={{
                                      backgroundColor:
                                        planColors[index % planColors.length],
                                      textAlign: "center",
                                    }}
                                  >
                                    {plan.name.charAt(0).toUpperCase() +
                                      plan.name.slice(1)}
                                  </TableCell>
                                )}
                                <TableCell align="center">
                                  {frequency}
                                </TableCell>
                                {frequency !== "Monthly" ? (
                                  <TableCell align="center">
                                    {initialPricingData.discounts[
                                      `${plan.name}-${frequency}`
                                    ] || "-"}
                                  </TableCell>
                                ) : (
                                  <TableCell align="center">-</TableCell>
                                )}
                                <TableCell align="center">
                                  {initialPricingData.prices[
                                    `${plan.name}-${frequency}`
                                  ] || "-"}
                                </TableCell>
                                {values.offerApplicable && (
                                  <>
                                    <TableCell align="center">
                                      {initialPricingData.offerPrices[
                                        `${plan.name}-${frequency}`
                                      ] || "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                      {initialPricingData.offerPercentages[
                                        `${plan.name}-${frequency}`
                                      ]
                                        ? initialPricingData.offerPercentages[
                                            `${plan.name}-${frequency}`
                                          ]
                                        : "-"}
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            ))
                          )
                        ) : (
                          <TableRow>
                            <TableCell align="center">
                              No plans available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    )}
                  </Table>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ textAlign: "center" }}>
                      No Plan Available
                    </Typography>
                  </>
                )}
              </TableContainer>
              <Grid container>
                <Grid item xs={12} sx={{ textAlign: "right", padding: 2 }}>
                  {manageMode !== "view" &&
                    manageMode !== "ongoing" &&
                    manageMode !== "create" && (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={resetFields}
                        sx={{ marginRight: 2 }}
                      >
                        Reset
                      </Button>
                    )}

                  {manageMode !== "view" && manageMode !== "ongoing" && (
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        height: "100%",
                        background:
                          "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
                        color: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                        textTransform: "uppercase",
                        fontWeight: "bold",
                        "&:hover": {
                          background:
                            "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
                          boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.3)",
                        },
                      }}
                    >
                      {manageMode !== "create" ? "Update" : "Submit"}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Card>
    </Container>
  );
};

export default PlanTable;
